import React, { useState, useEffect } from 'react';
import { validateEmail, EmailValidationResult } from '../utils/emailValidation';

interface EmailInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  placeholder?: string;
  required?: boolean;
  label?: string;
  showLabel?: boolean;
  id?: string;
  name?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  showValidationFeedback?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function EmailInput({
  value,
  onChange,
  onValidationChange,
  placeholder = 'Enter your email address',
  required = true,
  label = 'Email Address',
  showLabel = true,
  id,
  name = 'email',
  autoFocus = false,
  disabled = false,
  showValidationFeedback = true,
  className = '',
  style
}: EmailInputProps) {
  const [validationResult, setValidationResult] = useState<EmailValidationResult | null>(null);
  const [hasBlurred, setHasBlurred] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Validate on change (but only show errors after blur or if invalid format)
  useEffect(() => {
    if (value) {
      const result = validateEmail(value);
      setValidationResult(result);
      onValidationChange?.(result.isValid);
    } else {
      setValidationResult(null);
      onValidationChange?.(false);
    }
  }, [value, onValidationChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleBlur = () => {
    setHasBlurred(true);
    setIsFocused(false);
    if (value) {
      const result = validateEmail(value);
      setValidationResult(result);
      onValidationChange?.(result.isValid);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleSuggestionClick = () => {
    if (validationResult?.suggestion) {
      onChange(validationResult.suggestion);
    }
  };

  // Determine if we should show error
  const shouldShowError = hasBlurred && validationResult && !validationResult.isValid;
  const isValid = validationResult?.isValid === true;
  const showSuccess = hasBlurred && isValid && value.length > 0;

  // Determine border color
  let borderColor = '#d1d5db'; // default gray
  if (isFocused) {
    borderColor = showSuccess ? '#10b981' : '#D8B725'; // green if valid, orange if focused
  } else if (shouldShowError) {
    borderColor = '#dc2626'; // red
  } else if (showSuccess) {
    borderColor = '#10b981'; // green
  }

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {showLabel && (
        <label
          htmlFor={id || name}
          style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '600',
            color: 'var(--dark)',
            fontFamily: 'var(--montserrat)'
          }}
        >
          {label} {required && '*'}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        <input
          type="email"
          id={id || name}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          required={required}
          autoFocus={autoFocus}
          disabled={disabled}
          autoComplete="email"
          style={{
            width: '100%',
            padding: '0.75rem',
            paddingRight: showValidationFeedback && (showSuccess || shouldShowError) ? '2.5rem' : '0.75rem',
            border: `2px solid ${borderColor}`,
            borderRadius: '8px',
            fontSize: '1rem',
            fontFamily: 'var(--raleway)',
            transition: 'all 0.2s ease',
            outline: 'none',
            ...style
          }}
          className={className}
        />
        
        {/* Validation icon */}
        {showValidationFeedback && (
          <div
            style={{
              position: 'absolute',
              right: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              pointerEvents: 'none'
            }}
          >
            {showSuccess && (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#10b981"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
            {shouldShowError && (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#dc2626"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            )}
          </div>
        )}
      </div>

      {/* Error message */}
      {shouldShowError && validationResult?.error && (
        <div
          style={{
            marginTop: '0.5rem',
            color: '#dc2626',
            fontSize: '0.875rem',
            fontFamily: 'var(--montserrat)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5rem'
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0, marginTop: '2px' }}
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{validationResult.error}</span>
        </div>
      )}

      {/* Suggestion */}
      {shouldShowError && validationResult?.suggestion && (
        <div
          style={{
            marginTop: '0.5rem',
            padding: '0.75rem',
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontFamily: 'var(--montserrat)',
            color: 'var(--dark)'
          }}
        >
          <div style={{ marginBottom: '0.5rem', fontWeight: '600' }}>
            Did you mean:
          </div>
          <button
            type="button"
            onClick={handleSuggestionClick}
            style={{
              background: 'none',
              border: 'none',
              color: '#2563eb',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontFamily: 'var(--montserrat)',
              fontSize: '0.875rem',
              padding: 0
            }}
          >
            {validationResult.suggestion}
          </button>
        </div>
      )}

      {/* Success message */}
      {showSuccess && (
        <div
          style={{
            marginTop: '0.5rem',
            color: '#10b981',
            fontSize: '0.875rem',
            fontFamily: 'var(--montserrat)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
          <span>Email address looks good!</span>
        </div>
      )}
    </div>
  );
}

